import {
  IDataFetcherResponseError,
  StoreMutationType,
} from "@common/utils/use-query/types";
import { purchasesStore } from "@stores/purchases";
import { IChangePlanData, IPurchase } from "@stores/purchases/types";
import { useCallback, useEffect } from "react";
import { MutateOptions } from "@tanstack/react-query";
import { usePlanChange } from "./usePlanChange";

// Everything shared between add-ons — the one-change-at-a-time lock and the
// restart window — belongs to the surrounding <PlanChangeProvider>.
type IUseChangePlanWithInstanceRestartProps = Pick<IPurchase, "id">;

export default function useChangePlanWithInstanceRestart({
  id,
}: IUseChangePlanWithInstanceRestartProps): Pick<
  StoreMutationType<IPurchase, unknown, IChangePlanData>,
  "isPending" | "isSuccess" | "error" | "mutate"
> & { isUpdatingSubscription: boolean } {
  const {
    isPending,
    isSuccess,
    error,
    mutate: changePurchasePlan,
  } = purchasesStore.useChangePlan(id);

  const { isChangingPlan, registerPendingChange, startRestartWindow } =
    usePlanChange();

  // Publish this add-on's in-flight state so the whole group locks. The
  // cleanup also covers unmounting mid-request.
  useEffect(() => {
    if (!isPending) {
      return;
    }

    return registerPendingChange();
  }, [isPending, registerPendingChange]);

  const changePlan = useCallback(
    (
      changesData: IChangePlanData,
      options?: MutateOptions<
        IPurchase,
        IDataFetcherResponseError<unknown>,
        IChangePlanData,
        unknown
      >,
    ) => {
      changePurchasePlan(changesData, {
        ...options,
        onSuccess(data, variables, onMutateResult, context) {
          startRestartWindow();

          if (typeof options?.onSuccess === "function") {
            options.onSuccess(data, variables, onMutateResult, context);
          }
        },
      });
    },
    [changePurchasePlan, startRestartWindow],
  );

  return {
    // Any change under the provider locks every add-on's controls. The restart
    // window deliberately does not: the banner covers it, and a change that
    // lands during one just refreshes the window.
    isPending: isPending || isChangingPlan,
    isUpdatingSubscription: isPending, // this add-on's own API call in flight
    isSuccess,
    error,
    mutate: changePlan,
  };
}
